import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Percent, DollarSign, History, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = {
  broker: "Inmobiliaria / Broker",
  accountant: "Contador",
  insurance: "Seguro",
  utility: "Servicios",
  expenses: "Expensas",
  other: "Otro",
};

const emptyForm = (propertyId) => ({
  property_id: propertyId,
  provider_id: "",
  category: "broker",
  name: "",
  amount_type: "percentage_of_rent",
  amount: "",
  currency: "USD",
  active: true,
  notes: "",
});

export default function RecurringExpensesSection({ propertyId, propertyCurrency = "USD" }) {
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(propertyId));

  const load = async () => {
    const [r, p] = await Promise.all([
      api.get(`/recurring?property_id=${propertyId}`),
      api.get(`/providers`),
    ]);
    setItems(r.data);
    setProviders(p.data);
  };

  useEffect(() => { if (propertyId) load(); /* eslint-disable-next-line */ }, [propertyId]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm(propertyId), currency: propertyCurrency }); setDlgOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, amount: String(r.amount) }); setDlgOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        provider_id: form.provider_id || null,
      };
      if (editing) {
        await api.put(`/recurring/${editing.id}`, payload);
        toast.success("Cargo recurrente actualizado");
      } else {
        await api.post("/recurring", payload);
        toast.success("Cargo recurrente creado — se generará automáticamente al cobrar el alquiler");
      }
      setDlgOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`¿Eliminar "${r.name}"?`)) return;
    try {
      await api.delete(`/recurring/${r.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const providerMap = Object.fromEntries(providers.map((p) => [p.id, p]));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Cargos recurrentes</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Se generan automáticamente como gastos cada vez que registrás un pago de alquiler.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={openNew} data-testid="recurring-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Agregar
        </Button>
      </div>
      <div className="grid gap-2">
        {items.map((r) => {
          const prov = providerMap[r.provider_id];
          return (
            <div key={r.id} className={`rounded-lg border p-3 ${r.active ? "border-border/60 bg-card" : "border-border/60 bg-card/50 opacity-60"}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {CAT_LABEL[r.category] || r.category}{prov ? ` · ${prov.name}` : ""}
                    {!r.active && " · inactivo"}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {r.applied_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-500 border border-emerald-500/30 bg-emerald-500/5 rounded-full px-2 py-0.5">
                        <CheckCircle2 className="w-3 h-3" /> Aplicado {r.applied_count} {r.applied_count === 1 ? "vez" : "veces"}
                        {r.last_applied && ` · último ${r.last_applied}`}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground border border-border/60 rounded-full px-2 py-0.5">
                        <History className="w-3 h-3" /> Aún no aplicado
                      </span>
                    )}
                    {r.total_usd_applied > 0 && (
                      <span className="text-[10px] text-muted-foreground num">
                        Total USD: ${r.total_usd_applied.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-base font-semibold num inline-flex items-center gap-1">
                    {r.amount_type === "percentage_of_rent" ? (
                      <><Percent className="w-3.5 h-3.5" />{r.amount.toFixed(2)}%</>
                    ) : (
                      <><DollarSign className="w-3.5 h-3.5" />{r.amount.toFixed(2)} {r.currency}</>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
                    {r.amount_type === "percentage_of_rent" ? "sobre alquiler" : "monto fijo"}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="text-rose-500 hover:text-rose-500" onClick={() => del(r)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          );
        })}
        {!items.length && (
          <div className="text-center py-6 text-muted-foreground text-sm rounded-lg border border-dashed border-border/60">
            Sin cargos recurrentes. Ej: comisión inmobiliaria 5% del alquiler.
          </div>
        )}
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar" : "Nuevo"} cargo recurrente</DialogTitle>
            <DialogDescription>
              Se genera automáticamente cuando registrás el pago del alquiler de esta propiedad.
              {editing && (
                <span className="block mt-1 text-amber-600 dark:text-amber-400">
                  ⚠️ Los cambios aplican desde ahora en adelante. Los meses ya cobrados conservan su tasa original —
                  si necesitás ajustar meses pasados, editá manualmente esas transacciones en Movimientos.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Descripción</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Comisión Inmobiliaria Palermo" />
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CAT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proveedor (opcional)</Label>
              <Select value={form.provider_id || "none"} onValueChange={(v) => setForm({ ...form, provider_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguno</SelectItem>
                  {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Tipo de monto</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, amount_type: "percentage_of_rent" })}
                  className={`p-3 rounded-lg border text-left transition-colors ${form.amount_type === "percentage_of_rent" ? "border-foreground/40 bg-secondary" : "border-border/60 hover:border-foreground/20"}`}
                >
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4" />
                    <div className="font-medium text-sm">Porcentaje del alquiler</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Ideal para comisión inmobiliaria (ej: 5% sobre el alquiler cobrado).</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, amount_type: "fixed" })}
                  className={`p-3 rounded-lg border text-left transition-colors ${form.amount_type === "fixed" ? "border-foreground/40 bg-secondary" : "border-border/60 hover:border-foreground/20"}`}
                >
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    <div className="font-medium text-sm">Monto fijo</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Ideal para honorarios contador, seguros, etc.</div>
                </button>
              </div>
            </div>
            <div>
              <Label>{form.amount_type === "percentage_of_rent" ? "Porcentaje (%)" : "Monto"}</Label>
              <Input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            {form.amount_type === "fixed" && (
              <div>
                <Label>Moneda</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="ARS">ARS</SelectItem>
                    <SelectItem value="MXN">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2 flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <div>
                <div className="text-sm">Activo</div>
                <div className="text-[11px] text-muted-foreground">Si está apagado, no se genera al cobrar.</div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
